import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import WebPlayback from './WebPlayback';
import UnifiedFloatingPlayer from './UnifiedFloatingPlayer';
import { apiUrl } from './config';

// Helper: Pause Spotify if currently playing
const pauseSpotifyIfPlaying = (spotifyPlayer) => {
  if (!spotifyPlayer) return;
  try {
    spotifyPlayer.getCurrentState?.()
      .then((state) => {
        if (state && !state.paused) {
          try { spotifyPlayer.togglePlay?.(); } catch (_) { /* ignore */ }
        }
      })
      .catch(() => {
        try { spotifyPlayer.togglePlay?.(); } catch (_) { /* ignore */ }
      });
  } catch (_) { /* ignore */ }
};

// Helper: Pause YouTube if currently playing
const pauseYoutubeIfPlaying = (youtubePlayer) => {
  if (!youtubePlayer) return;
  try { youtubePlayer.pause?.(); } catch (_) { /* ignore */ }
};

// Navbar Component
const Navbar = () => {
  return (
    <nav className="navbar">
      <div className="navbar__container">
        <a href="/" id="navbar__logo">
          <img
            src={`${process.env.PUBLIC_URL || ''}/favicon.ico`}
            alt=""
            className="navbar__icon"
          />
          <span className="navbar__title">Pomoducky</span>
        </a>
      </div>
    </nav>
  );
};

// VolumePopup Component
const VolumePopup = ({ show, volume, onVolumeChange, soundKey }) => {
  const handleVolumeChange = (e) => {
    onVolumeChange(parseInt(e.target.value));
  };

  return (
    <div 
      className={`volume-popup ${show ? 'show' : ''}`}
    >
      <div className="volume-control">
        <label htmlFor={`${soundKey}-volume`}>Volume:</label>
        <input
          type="range"
          id={`${soundKey}-volume`}
          className="volume-slider"
          min="0"
          max="100"
          value={volume}
          onChange={handleVolumeChange}
        />
      </div>
      <div className="popup-arrow"></div>
    </div>
  );
};

// SoundButton Component
const SoundButton = ({ soundKey, label, soundFile, icon }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(75);
  const [showPopup, setShowPopup] = useState(false);
  
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const gainNodeRef = useRef(null);
  const audioBufferRef = useRef(null);
  const startTimeRef = useRef(0);
  const offsetRef = useRef(0);
  const hideTimeoutRef = useRef(null);

  // Initialize audio context
  const initAudioContext = () => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioContextRef.current;
  };

  // Load audio file from sounds folder (use absolute URL so rewrites don't return HTML)
  const loadSound = async () => {
    if (audioBufferRef.current) return audioBufferRef.current;
    const url = `${window.location.origin}${process.env.PUBLIC_URL || ''}${soundFile}`;
    try {
      const context = initAudioContext();
      const response = await fetch(url);
      if (!response.ok) {
        console.error(`Sound file failed to load (${response.status}): ${url}`);
        return null;
      }
      const contentType = (response.headers.get('content-type') || '').toLowerCase();
      if (contentType.includes('text/html')) {
        console.error(`Sound URL returned HTML instead of audio (check Amplify rewrites): ${url}`);
        return null;
      }
      const arrayBuffer = await response.arrayBuffer();
      const buffer = await context.decodeAudioData(arrayBuffer);
      audioBufferRef.current = buffer;
      return buffer;
    } catch (error) {
      console.error(`Error loading sound file ${url}:`, error);
      return null;
    }
  };

  // Play sound with fade in
  const playSoundWithFade = (buffer, fadeTime = 1, offset = 0) => {
    const context = initAudioContext();
    
    // Check if context is still valid
    if (context.state === 'closed') {
      console.log('AudioContext is closed, cannot play sound');
      return;
    }
    
    const source = context.createBufferSource();
    const gain = context.createGain();
    
    source.buffer = buffer;
    source.loop = true;
    source.connect(gain);
    gain.connect(context.destination);
    
    // Start with 0 volume and fade in
    gain.gain.value = 0;
    source.start(0, offset);
    gain.gain.linearRampToValueAtTime(volume / 100, context.currentTime + fadeTime);
    
    sourceRef.current = source;
    gainNodeRef.current = gain;
    startTimeRef.current = context.currentTime;
  };

  // Stop sound with fade out
  const stopSoundWithFade = (fadeTime = 1) => {
    if (!gainNodeRef.current || !sourceRef.current) return;
    
    const context = audioContextRef.current;
    const gain = gainNodeRef.current;
    const source = sourceRef.current;
    
    // Calculate current offset for seamless resume
    const elapsed = context.currentTime - startTimeRef.current;
    offsetRef.current = (offsetRef.current + elapsed) % audioBufferRef.current.duration;
    
    // Fade out
    gain.gain.linearRampToValueAtTime(0, context.currentTime + fadeTime);
    
    setTimeout(() => {
      if (source.stop) source.stop();
      sourceRef.current = null;
      gainNodeRef.current = null;
    }, fadeTime * 1000);
  };

  // Toggle sound playback
  const toggleSound = async () => {
    // Resume AudioContext on first user gesture (required by browsers; suspended context produces no sound)
    const context = initAudioContext();
    if (context.state === 'suspended') {
      await context.resume();
    }

    const buffer = await loadSound();
    if (!buffer) return;

    if (isPlaying) {
      stopSoundWithFade(1);
      setIsPlaying(false);
    } else {
      playSoundWithFade(buffer, 1, offsetRef.current);
      setIsPlaying(true);
    }
  };

  // Update volume
  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    if (gainNodeRef.current) {
      gainNodeRef.current.gain.value = newVolume / 100;
    }
  };

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setShowPopup(true);
  };

  const handleMouseLeave = () => {
    hideTimeoutRef.current = setTimeout(() => {
      setShowPopup(false);
    }, 300);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
      if (sourceRef.current && sourceRef.current.stop) {
        sourceRef.current.stop();
      }
    };
  }, []);

  return (
    <div 
      className="sound-button-container"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{ display: 'inline-block', position: 'relative' }}
    >
      <button 
        className={`${soundKey}-btn${isPlaying ? ' playing' : ''}`}
        onClick={toggleSound}
      >
        <span className="sound-icon">{icon}</span>
        <span className="sound-label">{isPlaying ? 'Stop' : label}</span>
      </button>
      <VolumePopup
        show={showPopup}
        volume={volume}
        onVolumeChange={handleVolumeChange}
        soundKey={soundKey}
      />
    </div>
  );
};

// SoundControls Component
const SoundControls = () => {
  const soundConfigs = [
    {
      key: 'typing',
      label: 'Typing',
      icon: '⌨️',
      soundFile: '/Sounds/TypingSound.mp3'
    },
    {
      key: 'rain',
      label: 'Rain',
      icon: '🌧️',
      soundFile: '/Sounds/RainSound.m4a'
    },
    {
      key: 'brown',
      label: 'Brown Noise',
      icon: '🔊',
      soundFile: '/Sounds/BrownSound.mp3'
    }
  ];

  return (
    <div className="sound-controls">
      {soundConfigs.map(config => (
        <SoundButton
          key={config.key}
          soundKey={config.key}
          label={config.label}
          icon={config.icon}
          soundFile={config.soundFile}
        />
      ))}
    </div>
  );
};

// PomodoroTimer Component
const PomodoroTimer = () => {
  const [workDuration, setWorkDuration] = useState(25); // Work duration in minutes
  const [breakDuration] = useState(5); // Break duration in minutes
  const [timeLeft, setTimeLeft] = useState(25 * 60); // 25 minutes in seconds
  const [isBreak, setIsBreak] = useState(false);
  const [isPaused, setIsPaused] = useState(true);
  const [buttonText, setButtonText] = useState('Start');
  const intervalRef = useRef(null);

  // Calculate total duration based on current mode
  const totalDuration = isBreak ? breakDuration * 60 : workDuration * 60;
  
  // Calculate progress (0 to 1)
  const progress = 1 - (timeLeft / totalDuration);
  
  // SVG circle parameters
  const size = 320;
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - progress);

  // Handle duration input change
  const handleDurationChange = (e) => {
    const value = parseInt(e.target.value) || 1;
    const clampedValue = Math.max(1, Math.min(99, value));
    setWorkDuration(clampedValue);
    // Update timeLeft if timer hasn't started yet
    if (isPaused && buttonText === 'Start') {
      setTimeLeft(clampedValue * 60);
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  };

  // Clear interval on component unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Timer logic
  useEffect(() => {
    if (!isPaused && timeLeft > 0) {
      intervalRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            // Timer finished
            if (!isBreak) {
              // Start break
              setIsBreak(true);
              return 5 * 60; // 5 minute break
            } else {
              // End break, reset to work timer
              setIsBreak(false);
              setIsPaused(true);
              setButtonText('Start');
              return workDuration * 60; // Use custom work duration
            }
          }
          return prev - 1;
        });
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, timeLeft, isBreak, workDuration]);

  const togglePause = () => {
    if (isPaused) {
      setIsPaused(false);
      setButtonText('Pause');
    } else {
      setIsPaused(true);
      setButtonText('Resume');
    }
  };

  const reset = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    setIsBreak(false);
    setTimeLeft(workDuration * 60);
    setIsPaused(true);
    setButtonText('Start');
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      if (e.code === 'Space') {
        e.preventDefault();
        togglePause();
      } else if (e.code === 'KeyR') {
        e.preventDefault();
        reset();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isPaused, workDuration]);

  return (
    <>
      <div className="timer-section">
        <div className="timer-ring-container">
          <svg className="timer-ring" width={size} height={size}>
            {/* Background circle */}
            <circle
              className="timer-ring-bg"
              cx={size / 2}
              cy={size / 2}
              r={radius}
              strokeWidth={strokeWidth}
            />
            {/* Progress circle */}
            <circle
              className={`timer-ring-progress ${isBreak ? 'break' : 'work'}`}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              transform={`rotate(-90 ${size / 2} ${size / 2})`}
            />
          </svg>
          <div className="timer-display">{formatTime(timeLeft)}</div>
        </div>
        {isBreak && (
          <div className="break-indicator">
            <p>Break Time! 🎉</p>
          </div>
        )}
        <div className="keyboard-hints">
          <span>Space: Play/Pause</span>
          <span>R: Reset</span>
        </div>
      </div>
      <div className="timer-controls-fixed">
        <div className="timer-input-wrapper">
          <input
            type="number"
            className="timer-duration-input"
            value={workDuration}
            onChange={handleDurationChange}
            min="1"
            max="99"
            disabled={!isPaused || buttonText !== 'Start'}
          />
          <span className="timer-input-label">min</span>
        </div>
        <button className={`timer-tab${!isPaused ? ' active' : ''}`} onClick={togglePause}>
          <span className="timer-tab-icon">{isPaused ? '▶️' : '⏸️'}</span>
          <span className="timer-tab-label">{buttonText}</span>
        </button>
        <button className="timer-tab" onClick={reset}>
          <span className="timer-tab-icon">🔄</span>
          <span className="timer-tab-label">Reset</span>
        </button>
      </div>
    </>
  );
};

// Main App Component
function App() {
  const [service, setService] = useState('spotify'); // 'spotify' | 'youtube'
  const [token, setToken] = useState('');
  const [spotifyPlayer, setSpotifyPlayer] = useState(null);
  const [spotifyTrack, setSpotifyTrack] = useState(null);
  const [isSpotifyPaused, setIsSpotifyPaused] = useState(true);
  const [spotifyDeviceId, setSpotifyDeviceId] = useState(null);
  const [youtubeVideo, setYoutubeVideo] = useState(null);
  const [youtubePlayer, setYoutubePlayer] = useState(null);
  const [isYoutubePlaying, setIsYoutubePlaying] = useState(false);
  const [youtubeError, setYoutubeError] = useState(null);
  
  const tokenExpiresAtRef = useRef(0);
  const refreshTimerRef = useRef(null);

  // Function to fetch a fresh token from the server
  const fetchToken = async () => {
    try {
      const response = await fetch(apiUrl('/auth/token'));
      if (!response.ok) {
        console.warn('Auth token request failed:', response.status, response.statusText);
        return { access_token: '', expires_at: 0 };
      }
      const text = await response.text();
      if (!text || text.trim() === '') {
        return { access_token: '', expires_at: 0 };
      }
      return JSON.parse(text);
    } catch (err) {
      console.warn('Auth token fetch failed:', err);
      return { access_token: '', expires_at: 0 };
    }
  };

  // Function to refresh the token
  const refreshToken = async () => {
    try {
      console.log('Refreshing Spotify token...');
      const response = await fetch(apiUrl('/auth/refresh'), { method: 'POST' });
      if (!response.ok) {
        console.warn('Token refresh failed:', response.status);
        // If refresh fails, try getting the current token (server may have refreshed it)
        const current = await fetchToken();
        if (current.access_token) {
          setToken(current.access_token);
          tokenExpiresAtRef.current = current.expires_at || 0;
          scheduleTokenRefresh(current.expires_at);
        }
        return;
      }
      const data = await response.json();
      if (data.access_token) {
        setToken(data.access_token);
        tokenExpiresAtRef.current = data.expires_at || 0;
        scheduleTokenRefresh(data.expires_at);
        console.log('Token refreshed successfully');
      }
    } catch (err) {
      console.warn('Token refresh error:', err);
    }
  };

  // Schedule a token refresh before expiration
  const scheduleTokenRefresh = (expiresAt) => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    
    if (!expiresAt) return;
    
    // Refresh 5 minutes before expiration
    const refreshIn = Math.max((expiresAt - Date.now() - 300000), 60000);
    console.log(`Scheduling token refresh in ${Math.round(refreshIn / 1000)} seconds`);
    
    refreshTimerRef.current = setTimeout(() => {
      refreshToken();
    }, refreshIn);
  };

  // Store the latest token in a ref for getOAuthToken callback
  const tokenRef = useRef(token);
  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  // Callback for WebPlayback to get a fresh token (stable reference)
  const getOAuthToken = useCallback(async (callback) => {
    // If token is still valid (more than 1 minute left), use it
    if (tokenRef.current && tokenExpiresAtRef.current > Date.now() + 60000) {
      callback(tokenRef.current);
      return;
    }
    
    // Otherwise fetch a fresh token
    console.log('Token expired or missing, fetching fresh token...');
    const data = await fetchToken();
    if (data.access_token) {
      setToken(data.access_token);
      tokenRef.current = data.access_token;
      tokenExpiresAtRef.current = data.expires_at || 0;
      scheduleTokenRefresh(data.expires_at);
      callback(data.access_token);
    } else {
      // No valid token available
      callback('');
    }
  }, []);

  // Initial token fetch
  useEffect(() => {
    async function initToken() {
      const data = await fetchToken();
      setToken(data.access_token || '');
      tokenExpiresAtRef.current = data.expires_at || 0;
      if (data.access_token && data.expires_at) {
        scheduleTokenRefresh(data.expires_at);
      }
    }
    initToken();
    
    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, []);

  // Pause YouTube when Spotify starts playing (only if on Spotify tab)
  // Always try to pause YouTube regardless of isYoutubePlaying state, 
  // since the actual player might still be playing even if state says otherwise
  useEffect(() => {
    if (service === 'spotify' && !isSpotifyPaused && youtubePlayer) {
      pauseYoutubeIfPlaying(youtubePlayer);
      setIsYoutubePlaying(false);
    }
  }, [service, isSpotifyPaused, youtubePlayer]);

  // Pause Spotify when YouTube starts playing (only if on YouTube tab)
  useEffect(() => {
    if (service === 'youtube' && isYoutubePlaying && !isSpotifyPaused) {
      pauseSpotifyIfPlaying(spotifyPlayer);
    }
  }, [service, isYoutubePlaying, isSpotifyPaused, spotifyPlayer]);

  return (
    <div className="App">
      <Navbar />
      <div className="main">
        <div
          className="main__bg"
          style={{
            backgroundImage: `url(${process.env.PUBLIC_URL || ''}/images/twoducks.png)`,
          }}
        />
        <div className="main__container">
          <div className="main__content">
            {token !== '' && (
              <WebPlayback 
                token={token}
                getOAuthToken={getOAuthToken}
                onPlayerReady={setSpotifyPlayer}
                onTrackChange={setSpotifyTrack}
                onPausedChange={setIsSpotifyPaused}
                onDeviceId={setSpotifyDeviceId}
              />
            )}
            <PomodoroTimer />
            <SoundControls />
          </div>
        </div>
      </div>
      <UnifiedFloatingPlayer
        service={service}
        onServiceChange={setService}
        onBeforeSwitchToSpotify={() => {
          pauseYoutubeIfPlaying(youtubePlayer);
          setIsYoutubePlaying(false);
        }}
        onBeforeSwitchToYoutube={() => {
          if (!isSpotifyPaused) {
            pauseSpotifyIfPlaying(spotifyPlayer);
          }
        }}
        spotifyEnabled={token !== ''}
        spotifyPlayer={spotifyPlayer}
        spotifyTrack={spotifyTrack}
        isSpotifyPaused={isSpotifyPaused}
        spotifyDeviceId={spotifyDeviceId}
        youtubeVideo={youtubeVideo}
        isYoutubePlaying={isYoutubePlaying}
        onYoutubePlayerReady={setYoutubePlayer}
        onYoutubeStateChange={setIsYoutubePlaying}
        youtubeApiError={youtubeError}
        onYoutubeSearchError={setYoutubeError}
        onYoutubeVideoSelect={(video) => {
          setYoutubeError(null);
          if (!isSpotifyPaused) {
            pauseSpotifyIfPlaying(spotifyPlayer);
          }
          setYoutubeVideo(video);
          setService('youtube');
        }}
        onMaximizeSearch={null}
      />
    </div>
  );
}

export default App;